import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, BarChart3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useFetchIndexFundHoldings,
  useCreateIndexFundHolding,
  useUpdateIndexFundHolding,
  useDeleteIndexFundHolding,
} from '@/hooks/securities/indexfunds/useIndexFundHoldings.ts';
import { useFetchIndexFundPrice } from '@/hooks/securities/indexfunds/useFetchIndexFundPrice.ts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { IndexFundForm } from '@/components/index-fund-form/IndexFundForm';
import { IndexFundMetricsCards } from '@/components/index-fund-metrics-cards/IndexFundMetricsCards';
import { IndexFundHoldingsTable } from '@/components/index-fund-holdings-table/IndexFundHoldingsTable';
import { IndexFundPortfolioTrendChart } from '@/components/index-fund-portfolio-trend-chart/IndexFundPortfolioTrendChart';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeaderSkeleton, AccountCardSkeleton, ChartSkeleton } from '@/components/LoadingSkeletons';
import type { IndexFundFormValues } from '@/components/index-fund-form/indexFundForm.schema';
import { SUPPORTED_CURRENCIES } from '@/components/index-fund-form/indexFundForm.schema';
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";

/**
 * Inner component that fetches prices for all holdings.
 * Separated so we can call hooks per holding symbol.
 */
function HoldingPriceLoader({
  symbol,
  onPrice,
}: {
  symbol: string;
  onPrice: (symbol: string, price: IndexFundPrice | null, isLoading: boolean) => void;
}) {
  const { data, isLoading } = useFetchIndexFundPrice(symbol);
  // Report back via callback — this is a render-effect pattern
  // We use useMemo to avoid infinite loops
  useMemo(() => {
    onPrice(symbol, data ?? null, isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, data, isLoading]);
  return null;
}

/**
 * IndexFundsPage — overview page at /investments/securities/index-funds.
 * Composes metrics cards, holdings table, portfolio trend chart, and form/delete dialogs.
 */
export default function IndexFundsPage() {
  const navigate = useNavigate();

  // Dialog / form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingHolding, setEditingHolding] = useState<IndexFundHolding | null>(null);
  const [deletingHolding, setDeletingHolding] = useState<IndexFundHolding | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Price state — collected from per-holding loaders
  const [prices, setPrices] = useState<Record<string, IndexFundPrice | null>>({});
  const [pricesLoading, setPricesLoading] = useState<Record<string, boolean>>({});

  // Holdings data
  const { data: holdings = [], isLoading, error, refetch } = useFetchIndexFundHoldings();

  const filteredHoldings = useMemo(() => {
    if (!searchQuery) return holdings;
    const lowerQuery = searchQuery.toLowerCase();
    return holdings.filter(h => 
      h.fund_name?.toLowerCase().includes(lowerQuery) ||
      h.fund_symbol?.toLowerCase().includes(lowerQuery) ||
      h.account_number?.toLowerCase().includes(lowerQuery) ||
      h.member_number?.toLowerCase().includes(lowerQuery) ||
      h.asset_class?.toLowerCase().includes(lowerQuery)
    );
  }, [holdings, searchQuery]);

  // Default currency: first holding's currency, or USD
  const defaultCurrency = holdings.length > 0 ? holdings[0].currency : 'USD';
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(undefined);
  const displayCurrency = selectedCurrency ?? defaultCurrency;

  // Exchange rates for currency conversion
  const { convert, isLoading: ratesLoading } = useExchangeRates(displayCurrency);

  // Stable convertAmount function passed to metrics cards
  const convertAmount = useMemo(
    () => (amount: number, from: string) => {
      if (from === displayCurrency) return amount;
      try {
        return convert(amount, from, displayCurrency);
      } catch {
        return amount;
      }
    },
    [convert, displayCurrency]
  );

  // Mutations
  const createHolding = useCreateIndexFundHolding();
  const updateHolding = useUpdateIndexFundHolding();
  const deleteHolding = useDeleteIndexFundHolding();

  // Unique symbols for price loading
  const uniqueSymbols = useMemo(
    () => [...new Set(holdings.map((h) => h.fund_symbol))],
    [holdings]
  );

  const handlePriceUpdate = useMemo(
    () => (symbol: string, price: IndexFundPrice | null, loading: boolean) => {
      setPrices((prev) => {
        if (prev[symbol] === price) return prev;
        return { ...prev, [symbol]: price };
      });
      setPricesLoading((prev) => {
        if (prev[symbol] === loading) return prev;
        return { ...prev, [symbol]: loading };
      });
    },
    []
  );

  const handleSaveHolding = async (data: IndexFundFormValues) => {
    try {
      if (editingHolding) {
        await updateHolding.mutateAsync({ id: editingHolding.id, ...data });
        toast.success('Index fund holding updated');
        setStatusMessage('Index fund holding updated');
      } else {
        await createHolding.mutateAsync(data);
        toast.success('Index fund holding added');
        setStatusMessage('Index fund holding added');
      }
      setFormOpen(false);
      setEditingHolding(null);
    } catch {
      const msg = editingHolding
        ? 'Failed to update index fund holding'
        : 'Failed to add index fund holding';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingHolding) return;
    try {
      await deleteHolding.mutateAsync(deletingHolding.id);
      toast.success('Index fund holding deleted');
      setStatusMessage('Index fund holding deleted');
      setDeletingHolding(null);
    } catch {
      toast.error('Failed to delete index fund holding');
    }
  };

  const hasHoldings = holdings.length > 0;

  // Prices map with non-null values for metrics/table (null entries treated as missing)
  const pricesForComponents = useMemo<Record<string, IndexFundPrice>>(() => {
    const result: Record<string, IndexFundPrice> = {};
    for (const [symbol, price] of Object.entries(prices)) {
      if (price !== null) result[symbol] = price;
    }
    return result;
  }, [prices]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite" data-testid="loading-skeleton">
        <PageHeaderSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <AccountCardSkeleton key={i} />
          ))}
        </div>
        <AccountCardSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Failed to load index fund holdings.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      {/* Hidden price loaders — one per unique symbol */}
      {uniqueSymbols.map((symbol) => (
        <HoldingPriceLoader key={symbol} symbol={symbol} onPrice={handlePriceUpdate} />
      ))}

      <div className="space-y-6">
        {/* SR live region */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Index Funds</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Track your index fund holdings and portfolio performance
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search index funds..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
              {/* Currency Toggle */}
              <CurrencyToggle
                baseCurrency={defaultCurrency}
                availableCurrencies={[...SUPPORTED_CURRENCIES]}
                selectedCurrency={displayCurrency}
                onCurrencyChange={setSelectedCurrency}
                showBalance={false}
                disabled={ratesLoading}
              />
              <Button
                onClick={() => {
                  setEditingHolding(null);
                  setFormOpen(true);
                }}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add new index fund holding"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        {hasHoldings && (
          <IndexFundMetricsCards
            holdings={filteredHoldings}
            prices={pricesForComponents}
            isLoading={false}
            currency={displayCurrency}
            convertAmount={convertAmount}
          />
        )}

        {/* Holdings Table or Empty State */}
        {hasHoldings ? (
          <>
            <IndexFundHoldingsTable
              holdings={filteredHoldings}
              prices={prices}
              pricesLoading={pricesLoading}
              onEdit={(holding) => {
                setEditingHolding(holding);
                setFormOpen(true);
              }}
              onDelete={(holding) => setDeletingHolding(holding)}
              onRowClick={(holding) =>
                navigate(`/investments/securities/index-funds/${holding.id}`)
              }
            />

            {/* Portfolio Trend Chart */}
            <IndexFundPortfolioTrendChart />
          </>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
              <p className="text-base sm:text-lg text-muted-foreground mb-2">
                No index fund holdings yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Add your first index fund to start tracking NAV, performance, and portfolio trends
              </p>
              <Button
                onClick={() => {
                  setEditingHolding(null);
                  setFormOpen(true);
                }}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Index Fund
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add / Edit Dialog */}
        <Dialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingHolding(null);
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>
                {editingHolding ? 'Edit Index Fund Holding' : 'Add Index Fund Holding'}
              </DialogTitle>
              <DialogDescription>
                {editingHolding
                  ? 'Update the details of your index fund holding.'
                  : 'Add a new index fund to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <IndexFundForm
              holding={editingHolding ?? undefined}
              onSave={handleSaveHolding}
              onCancel={() => {
                setFormOpen(false);
                setEditingHolding(null);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog
          open={!!deletingHolding}
          onOpenChange={(open) => !open && setDeletingHolding(null)}
        >
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete index fund holding?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This will permanently delete{' '}
                <strong>{deletingHolding?.fund_name ?? 'this holding'}</strong> from your portfolio.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel className="min-h-[44px] w-full sm:w-auto">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
